import React, { useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getCampaignCompletionReport } from '../../data/reportHelpers';
import { getStatusColor } from '../../data/helpers';
import SummaryStatBar from './SummaryStatBar';
import ExportBar from './ExportBar';

const { FiBookOpen, FiCheckCircle, FiClock, FiAlertTriangle, FiUsers } = FiIcons;

const typeColor = {
  Mandatory: 'bg-blue-100 text-blue-700',
  Remedial: 'bg-orange-100 text-orange-700',
  Onboarding: 'bg-purple-100 text-purple-700',
  Compliance: 'bg-teal-100 text-teal-700',
};

const COLUMNS = [
  { key: 'name', label: 'Campaign Name' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'endDate', label: 'End Date' },
  { key: 'totalAssigned', label: 'Assigned' },
  { key: 'completed', label: 'Completed' },
  { key: 'inProgress', label: 'In Progress' },
  { key: 'notStarted', label: 'Not Started' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'avgCompletion', label: 'Avg Completion %' },
];

const CampaignCompletionReport = ({ filters, raw }) => {
  const data = useMemo(() => getCampaignCompletionReport(raw, filters), [raw, filters]);

  const stats = [
    { label: 'Total Campaigns', value: data.length, icon: FiBookOpen, iconColor: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
    { label: 'Fully Completed', value: data.reduce((a, b) => a + b.completed, 0), icon: FiCheckCircle, iconColor: 'bg-emerald-50 text-emerald-600', border: 'border-emerald-100' },
    { label: 'Avg. Completion', value: `${Math.round(data.reduce((a, b) => a + b.avgCompletion, 0) / (data.length || 1))}%`, icon: FiClock, iconColor: 'bg-violet-50 text-violet-600', border: 'border-violet-100' },
    { label: 'Overdue Learners', value: data.reduce((a, b) => a + b.overdue, 0), icon: FiAlertTriangle, iconColor: 'bg-red-50 text-red-600', border: 'border-red-100' },
    { label: 'Total Assigned', value: data.reduce((a, b) => a + b.totalAssigned, 0), icon: FiUsers, iconColor: 'bg-amber-50 text-amber-600', border: 'border-amber-100' },
    { label: 'Completion Rate', value: `${Math.round(data.reduce((a, b) => a + b.completed, 0) / Math.max(data.reduce((a, b) => a + b.totalAssigned, 0), 1) * 100)}%`, icon: FiCheckCircle, iconColor: 'bg-teal-50 text-teal-600', border: 'border-teal-100' },
  ];

  const pdfStats = stats.map(s => ({ label: s.label, value: s.value }));

  return (
    <div className="space-y-4">
      <SummaryStatBar stats={stats} />
      <ExportBar reportTitle="Campaign Completion Report" data={data} columns={COLUMNS} summaryStats={pdfStats} rowCount={data.length} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Campaign</th>
                <th className="p-4">Type</th>
                <th className="p-4">Status</th>
                <th className="p-4">Assigned</th>
                <th className="p-4">Completed</th>
                <th className="p-4">In Progress</th>
                <th className="p-4">Overdue</th>
                <th className="p-4">Avg. Completion</th>
                <th className="p-4">Due Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {data.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4">
                    <p className="font-medium text-slate-800 max-w-48">{row.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{row.startDate} → {row.endDate}</p>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${typeColor[row.type] || 'bg-slate-100 text-slate-600'}`}>{row.type}</span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(row.status)}`}>{row.status}</span>
                  </td>
                  <td className="p-4 text-slate-700 font-medium">{row.totalAssigned}</td>
                  <td className="p-4 text-emerald-600 font-semibold">{row.completed}</td>
                  <td className="p-4 text-blue-600 font-semibold">{row.inProgress}</td>
                  <td className="p-4">
                    {row.overdue > 0 ? <span className="text-red-600 font-semibold">{row.overdue}</span> : <span className="text-slate-400">0</span>}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-slate-100 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${row.avgCompletion === 100 ? 'bg-emerald-500' : row.avgCompletion >= 60 ? 'bg-blue-500' : 'bg-orange-400'}`}
                          style={{ width: `${row.avgCompletion}%` }} />
                      </div>
                      <span className="text-xs font-bold text-slate-700">{row.avgCompletion}%</span>
                    </div>
                  </td>
                  <td className="p-4 text-xs text-slate-500">{row.endDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          {data.length} campaigns
        </div>
      </div>
    </div>
  );
};

export default CampaignCompletionReport;